import React, { useState, useRef, useCallback } from 'react';
import { usersApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface ProfilePictureUploadProps {
  onProfilePictureUpdated?: (url: string) => void;
  className?: string;
}

interface UploadedImage {
  file: File;
  url: string;
  filename: string;
  width: number;
  height: number;
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({ 
  onProfilePictureUpdated, 
  className = '' 
}) => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);
  
  const [showModal, setShowModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<UploadedImage | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Simple crop coordinates in pixels
  const [crop, setCrop] = useState({
    x: 50,
    y: 50,
    size: 200
  });

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    
    const img = new Image();
    img.onload = () => {
      const uploadedImg: UploadedImage = {
        file,
        url,
        filename: file.name,
        width: img.width,
        height: img.height
      };
      
      // Center the crop area
      const cropSize = Math.min(img.width, img.height) * 0.6;
      setCrop({
        x: (img.width - cropSize) / 2,
        y: (img.height - cropSize) / 2,
        size: cropSize
      });
      
      setUploadedImage(uploadedImg);
      setShowModal(true);
    };
    img.src = url;
  }, []);

  const resetModal = () => {
    setShowModal(false);
    setUploadedImage(null);
    setIsUploading(false);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Simple drag handler
  const handleDrag = (e: React.MouseEvent) => {
    if (!uploadedImage) return;
    e.preventDefault();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...crop };
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      const newX = Math.max(0, Math.min(uploadedImage.width - crop.size, startCrop.x + deltaX));
      const newY = Math.max(0, Math.min(uploadedImage.height - crop.size, startCrop.y + deltaY));
      
      setCrop({ ...crop, x: newX, y: newY });
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Simple resize handler
  const handleResize = (e: React.MouseEvent) => {
    if (!uploadedImage) return;
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...crop };
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      const avgDelta = (deltaX + deltaY) / 2;
      
      const newSize = Math.max(50, Math.min(
        Math.min(uploadedImage.width - crop.x, uploadedImage.height - crop.y),
        startCrop.size + avgDelta
      ));
      
      setCrop({ ...crop, size: newSize });
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleUploadAndCrop = async () => {
    if (!uploadedImage) return;

    try {
      setIsUploading(true);
      
      const uploadResponse = await usersApi.uploadProfilePicture(uploadedImage.file);
      if (!uploadResponse.data.success) {
        throw new Error('Upload failed');
      }

      const uploadedFilename = uploadResponse.data.data?.filename;
      if (!uploadedFilename) {
        throw new Error('No filename returned from upload');
      }

      setIsProcessing(true);
      
      // Use simple crop coordinates
      const cropResponse = await usersApi.cropProfilePicture({
        filename: uploadedFilename,
        x: Math.round(crop.x),
        y: Math.round(crop.y),
        width: Math.round(crop.size),
        height: Math.round(crop.size)
      });

      if (cropResponse.data.success && cropResponse.data.data?.profilePicture) {
        const newProfilePictureUrl = cropResponse.data.data.profilePicture;
        
        if (user) {
          updateUser({ profilePicture: newProfilePictureUrl });
        }
        
        onProfilePictureUpdated?.(newProfilePictureUrl);
        resetModal();
      } else {
        throw new Error('Failed to crop profile picture');
      }
    } catch (error: any) {
      console.error('Profile picture upload failed:', error);
      alert(`Failed to update profile picture: ${error.message}`);
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  };


  const getCurrentProfilePicture = () => {
    return user?.profilePicture;
  };

  const getInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  return (
    <>
      <div className={`flex flex-col items-center space-y-4 ${className}`}>
        {/* Profile Picture Display */}
        <div className="relative">
          <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
            {getCurrentProfilePicture() ? (
              <img
                src={getCurrentProfilePicture()}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error('Failed to load profile picture:', getCurrentProfilePicture());
                  // Hide the broken image and show initials instead
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
                onLoad={() => {
                  console.log('Profile picture loaded successfully:', getCurrentProfilePicture());
                }}
              />
            ) : null}
            {!getCurrentProfilePicture() && (
              <span className="text-2xl font-semibold text-slate-600 dark:text-slate-300">
                {getInitials()}
              </span>
            )}
          </div>
          
          {/* Upload Button Overlay */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 w-32 h-32 rounded-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn-secondary text-sm"
        >
          {getCurrentProfilePicture() ? 'Change Picture' : 'Upload Picture'}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Simple Crop Modal */}
      {showModal && uploadedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-4">Crop Profile Picture</h3>
              
              {/* Image with crop overlay */}
              <div className="mb-6">
                <div 
                  className="relative inline-block bg-gray-100 dark:bg-gray-700 mx-auto"
                  style={{
                    maxWidth: '500px',
                    maxHeight: '400px'
                  }}
                >
                  <img
                    ref={cropImageRef}
                    src={uploadedImage.url}
                    alt="Crop"
                    className="block max-w-full max-h-full"
                    draggable={false}
                  />
                  
                  {/* Simple crop overlay */}
                  <div
                    className="absolute border-2 border-blue-500 cursor-move"
                    style={{
                      left: crop.x,
                      top: crop.y,
                      width: crop.size,
                      height: crop.size,
                      backgroundColor: 'rgba(59, 130, 246, 0.1)'
                    }}
                    onMouseDown={handleDrag}
                  >
                    {/* Resize handle */}
                    <div
                      className="absolute -bottom-2 -right-2 w-4 h-4 bg-blue-500 border border-white cursor-se-resize"
                      onMouseDown={handleResize}
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <div className="mb-6">
                <label className="form-label">Preview</label>
                <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 mx-auto">
                  <div
                    className="w-full h-full"
                    style={{
                      backgroundImage: `url(${uploadedImage.url})`,
                      backgroundPosition: `-${crop.x * (96 / crop.size)}px -${crop.y * (96 / crop.size)}px`,
                      backgroundSize: `${uploadedImage.width * (96 / crop.size)}px ${uploadedImage.height * (96 / crop.size)}px`,
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={resetModal}
                  className="btn-secondary"
                  disabled={isUploading || isProcessing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadAndCrop}
                  disabled={isUploading || isProcessing}
                  className="btn-primary"
                >
                  {isUploading ? 'Uploading...' : isProcessing ? 'Processing...' : 'Save Profile Picture'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

